/**
 * LessonDetail (Sprint 3 B5 + B6) — page xem bài học có video.
 *
 * Sprint 3 B6: thêm
 *   - Prev/Next button (từ LessonDetail.prev_slug / next_slug — backend trả về)
 *   - Watch progress: resume từ localStorage, save mỗi 5s qua onTimeUpdate,
 *     clear khi xem hết qua onEnded.
 *   - Breadcrumb back về course.
 *
 * Em fetch 2 API song song:
 *   1. /api/catalog/lessons/<slug>/ — meta + nav
 *   2. /api/content/lessons/<slug>/video/playback/ — signed HLS (chỉ khi has_video)
 */

import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  message,
  Result,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  ClockCircleOutlined,
  HomeOutlined,
  LockOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  RobotOutlined,
} from "@ant-design/icons";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { FloatButton } from "antd";
import { useLessonDetail } from "@/api/catalog";
import {
  useVideoPlayback,
  type NotEnrolledError,
  type VideoNotReadyError,
} from "@/api/content";
import { useEnroll } from "@/api/enrollment";
import { useReportLessonWatched } from "@/api/integration";
import { useConversations, useCreateConversation, useGradeSrsCard } from "@/api/learning";
import { AITutorPanel } from "@/components/AITutorPanel";
import { LanguageLessonView } from "@/components/LanguageLessonView";
import { MarkdownContent } from "@/components/MarkdownContent";
import { ScormPlayer } from "@/components/ScormPlayer";
import { VideoPlayer } from "@/components/VideoPlayer";
import {
  clearProgress,
  getResumeTime,
  saveProgress,
} from "@/lib/watchProgress";

const { Title, Paragraph } = Typography;

interface AxiosErrorLike {
  response?: {
    status?: number;
    data?: VideoNotReadyError | NotEnrolledError;
  };
}

export function LessonDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  // Sprint 13 B2 — Citation highlight: nhận ?highlight=<chunk_order> từ AI Tutor.
  // Scroll to lesson-content + flash background 2s. Suppress repeat trigger
  // bằng useRef + chỉ trigger 1 lần per param change.
  const [searchParams] = useSearchParams();
  const highlightParam = searchParams.get("highlight");
  const highlightChunk = highlightParam !== null ? parseInt(highlightParam, 10) : null;
  const lastHighlightRef = useRef<number | null>(null);

  const { data: lesson, isLoading: lessonLoading } = useLessonDetail(slug);

  useEffect(() => {
    if (
      highlightChunk !== null &&
      lesson?.content &&
      lastHighlightRef.current !== highlightChunk
    ) {
      lastHighlightRef.current = highlightChunk;
      // Scroll sau khi DOM render content (microtask + smooth scroll)
      setTimeout(() => {
        const el = document.getElementById("lesson-content");
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [highlightChunk, lesson?.content]);
  // Chỉ fetch playback URL khi lesson đã load + has_video = true
  const {
    data: playback,
    isLoading: playbackLoading,
    error: playbackError,
    refetch: refetchPlayback,
  } = useVideoPlayback(lesson?.has_video ? slug : undefined);

  const enroll = useEnroll();

  // ─── AI Tutor (Sprint 7 B6) ─────────────────────────────────────────
  // Hooks PHẢI ở đầu component (trước early return) — React Rules of Hooks.
  // FloatButton bottom-right + Drawer panel. Auto-create conversation
  // per lesson nếu chưa có (lookup `useConversations(lesson_slug)`).
  const [tutorOpen, setTutorOpen] = useState(false);
  const { data: lessonConvs } = useConversations(slug);
  const createConv = useCreateConversation();
  const gradeSrs = useGradeSrsCard();
  // antd message qua hook (ConfigProvider có theme → static message không render;
  // hook + contextHolder mới hiển thị toast đúng theme).
  const [messageApi, messageHolder] = message.useMessage();
  const lessonConversationId = lessonConvs?.[0]?.id;

  const openTutor = async () => {
    if (!slug) return;
    if (!lessonConversationId && !createConv.isPending) {
      try {
        await createConv.mutateAsync(slug);
      } catch {
        /* ignore — panel render với placeholder */
      }
    }
    setTutorOpen(true);
  };

  const handleEnrollFromGate = async (courseSlug: string) => {
    try {
      await enroll.mutateAsync(courseSlug);
      messageApi.success("Đăng ký thành công! Đang tải video...");
      // Refetch playback — backend gate giờ pass
      await refetchPlayback();
    } catch {
      messageApi.error("Đăng ký thất bại. Vui lòng thử lại.");
    }
  };

  // Resume time đọc 1 lần khi mount — dùng useMemo để không tính lại mỗi render
  const startTime = useMemo(() => (slug ? getResumeTime(slug) : null), [slug]);

  // Sprint 18 B3 — xAPI heartbeat: gửi 1 POST khi user xem >50% (per session).
  // Track flag per lesson_id để reset khi navigate lesson khác.
  const reportLessonWatched = useReportLessonWatched();
  const xapiReportedRef = useRef<number | null>(null);
  useEffect(() => {
    // Reset flag khi mở lesson khác (slug change → lesson?.id change)
    xapiReportedRef.current = null;
  }, [slug]);

  // Cleanup throttle khi unmount — không bắt buộc nhưng clean
  useEffect(() => {
    return () => {
      // intentionally no-op; throttle map tự shrink khi browser GC
    };
  }, [slug]);

  if (lessonLoading) {
    return (
      <div style={{ textAlign: "center", padding: 64 }}>
        <Spin size="large" />
        <div style={{ marginTop: 12, color: "#7a8499" }}>
          Đang tải bài học...
        </div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <Result
        status="404"
        title="Không tìm thấy bài học"
        extra={<Link to="/courses">← Về danh sách môn học</Link>}
      />
    );
  }

  const errStatus = (playbackError as AxiosErrorLike | null)?.response?.status;
  const errData = (playbackError as AxiosErrorLike | null)?.response?.data;

  const handleTimeUpdate = (currentTime: number, duration: number) => {
    if (slug) saveProgress(slug, currentTime, duration);

    // Sprint 18 B3 — xAPI heartbeat: 1 lần / session khi >50% duration.
    // Guard: lesson?.id existed + duration > 0 + chưa report cho lesson này.
    if (
      lesson?.id &&
      duration > 0 &&
      currentTime / duration > 0.5 &&
      xapiReportedRef.current !== lesson.id
    ) {
      xapiReportedRef.current = lesson.id;
      reportLessonWatched.mutate({
        lesson_id: lesson.id,
        duration_seconds: Math.floor(currentTime),
      });
    }
  };

  const handleEnded = () => {
    if (slug) clearProgress(slug);
    // Auto-next nếu có lesson tiếp theo (UX YouTube/Coursera)
    if (lesson.next_slug) {
      setTimeout(() => navigate(`/lessons/${lesson.next_slug}`), 1500);
    }
  };

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto" }}>
      {messageHolder}
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <Link to="/"><HomeOutlined /></Link> },
          { title: <Link to="/courses">Môn học</Link> },
          {
            title: (
              <Link to={`/courses/${lesson.course_slug}`}>
                {lesson.course_name}
              </Link>
            ),
          },
          { title: lesson.title },
        ]}
      />

      <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div>
            <Tag icon={<PlayCircleOutlined />} color="blue">
              Bài {lesson.order}
            </Tag>
            <Title level={3} style={{ margin: "8px 0 0" }}>
              {lesson.title}
            </Title>
          </div>

          {/* ─── Video panel ─────────────────────────────────────────── */}
          {lesson.has_video && playbackLoading && (
            <div
              style={{
                textAlign: "center",
                padding: 64,
                background: "#0d1117",
                borderRadius: 8,
              }}
            >
              <Spin size="large" />
              <div style={{ color: "#7a8499", marginTop: 12 }}>
                Đang tải video...
              </div>
            </div>
          )}

          {lesson.has_video && playback && (
            <>
              <VideoPlayer
                src={playback.playback_url}
                poster={playback.thumbnail_url || lesson.video_thumbnail_url || undefined}
                title={lesson.title}
                startTime={startTime}
                onTimeUpdate={handleTimeUpdate}
                onEnded={handleEnded}
              />
              {playback.duration_seconds && (
                <Paragraph type="secondary" style={{ margin: 0 }}>
                  <ClockCircleOutlined />{" "}
                  Thời lượng: {Math.round(playback.duration_seconds / 60)} phút
                  {" · "}
                  Provider: <Tag>{playback.provider || "—"}</Tag>
                  {startTime !== null && (
                    <span style={{ marginLeft: 8 }}>
                      <Tag color="cyan">
                        Tiếp tục từ {Math.round(startTime / 60)} phút
                      </Tag>
                    </span>
                  )}
                </Paragraph>
              )}
            </>
          )}

          {/* ─── SCORM panel (Sprint 30) ─────────────────────────────── */}
          {lesson.scorm_package && (
            <ScormPlayer
              packageId={lesson.scorm_package.id}
              title={lesson.scorm_package.title}
            />
          )}

          {!lesson.has_video && !lesson.scorm_package && (
            <Alert
              type="info"
              showIcon
              message="Bài học này chưa có video"
              description="Giảng viên chưa upload video. Vẫn xem được nội dung markdown bên dưới."
            />
          )}

          {lesson.has_video && playbackError && errStatus === 409 && (
            <Alert
              type="warning"
              showIcon
              message="Video đang xử lý"
              description={`Trạng thái: ${(errData as VideoNotReadyError)?.video_status ?? "processing"}. Vui lòng quay lại sau vài phút.`}
            />
          )}

          {/* Sprint 4 B6: 403 not_enrolled → CTA đăng ký môn học */}
          {lesson.has_video && playbackError && errStatus === 403 &&
            (errData as NotEnrolledError)?.code === "not_enrolled" && (
              <Result
                icon={<LockOutlined style={{ color: "#1677ff" }} />}
                title="Cần đăng ký môn học để xem"
                subTitle={
                  errData?.detail ??
                  "Bài học này chỉ dành cho học viên đã đăng ký môn học."
                }
                extra={
                  <Space direction="vertical" align="center">
                    <Button
                      type="primary"
                      size="large"
                      icon={<PlusOutlined />}
                      loading={enroll.isPending}
                      onClick={() =>
                        handleEnrollFromGate(
                          (errData as NotEnrolledError).course_slug,
                        )
                      }
                    >
                      Đăng ký miễn phí
                    </Button>
                    <Link to={`/courses/${(errData as NotEnrolledError).course_slug}`}>
                      <Button type="link">Xem chi tiết môn học →</Button>
                    </Link>
                  </Space>
                }
              />
            )}

          {lesson.has_video &&
            playbackError &&
            errStatus !== 404 &&
            errStatus !== 409 &&
            errStatus !== 403 && (
              <Result
                status="error"
                title="Không tải được video"
                subTitle={String(playbackError)}
              />
            )}

          {/* ─── Markdown content (Sprint 4 B4: react-markdown) ────── */}
          {/* Sprint 13 B2 — Citation highlight: scroll + flash chunk
              được trích dẫn từ AI Tutor (?highlight=<chunk_order>). */}
          {(lesson.content || lesson.structured_payload) && (
            <Card
              type="inner"
              title="Nội dung bài học"
              id="lesson-content"
              data-testid="lesson-content"
              style={
                highlightChunk !== null
                  ? {
                      animation: "citation-flash 2s ease-out",
                      boxShadow: "0 0 0 3px #ffd54f",
                    }
                  : undefined
              }
            >
              <style>{`
                @keyframes citation-flash {
                  0%, 30% { background-color: #fff8e1; }
                  100% { background-color: transparent; }
                }
              `}</style>
              {lesson.structured_payload ? (
                <LanguageLessonView
                  payload={lesson.structured_payload}
                  lessonSlug={lesson.slug}
                  onGrade={(card, quality) => {
                    gradeSrs.mutate(
                      {
                        lessonSlug: lesson.slug,
                        frontJa: card.front_ja ?? "",
                        backVi: card.back_vi ?? "",
                        quality,
                      },
                      {
                        onSuccess: (r) =>
                          messageApi.success(`Đã chấm — ôn lại sau ${r.interval_days} ngày`),
                        onError: () => messageApi.error("Chấm thẻ thất bại, thử lại sau"),
                      },
                    );
                  }}
                />
              ) : (
                <MarkdownContent source={lesson.content} />
              )}
            </Card>
          )}

          {/* ─── Navigation prev / next ─────────────────────────────── */}
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            {lesson.prev_slug ? (
              <Link to={`/lessons/${lesson.prev_slug}`}>
                <Button icon={<ArrowLeftOutlined />}>Bài trước</Button>
              </Link>
            ) : (
              <Button icon={<ArrowLeftOutlined />} disabled>
                Bài trước
              </Button>
            )}
            <Link to={`/courses/${lesson.course_slug}`}>
              <Button type="default">Về môn học</Button>
            </Link>
            {lesson.next_slug ? (
              <Link to={`/lessons/${lesson.next_slug}`}>
                <Button type="primary" icon={<ArrowRightOutlined />}>
                  Bài tiếp theo
                </Button>
              </Link>
            ) : (
              <Button type="primary" icon={<ArrowRightOutlined />} disabled>
                Bài cuối
              </Button>
            )}
          </Space>
        </Space>
      </Card>

      {/* ─── AI Tutor floating button + Drawer (Sprint 7 B6) ───────── */}
      <FloatButton
        icon={<RobotOutlined />}
        type="primary"
        tooltip="Hỏi AI Tutor về bài này"
        onClick={openTutor}
        style={{
          right: 24,
          bottom: 24,
          background: "#722ed1",
        }}
        data-testid="ai-tutor-fab"
      />
      <AITutorPanel
        open={tutorOpen}
        onClose={() => setTutorOpen(false)}
        conversationId={lessonConversationId}
        lessonTitle={lesson.title}
      />
    </div>
  );
}
